/** Whether the Anonym-Ansicht (#140) is on, for this browser.
 *
 * It survives a reload and every open tab follows a change at once (the
 * `storage` event), but a new login starts with it off: the auth provider
 * calls `clear()` on sign-in, sign-out and session expiry. Read
 * synchronously, so a deep link never shows clear data for a frame. */

export const ANONYM_VIEW_KEY = "vidario.anonymView";

export interface AnonymViewStore {
  get: () => boolean;
  set: (on: boolean) => void;
  clear: () => void;
  subscribe: (listener: () => void) => () => void;
}

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

interface EventTargetLike {
  addEventListener: (type: "storage", listener: (e: { key: string | null }) => void) => void;
  removeEventListener: (type: "storage", listener: (e: { key: string | null }) => void) => void;
}

export function createAnonymViewStore(storage: StorageLike | null, target: EventTargetLike | null): AnonymViewStore {
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((l) => l());

  const get = (): boolean => {
    try {
      return storage?.getItem(ANONYM_VIEW_KEY) === "on";
    } catch {
      return false;
    }
  };
  const set = (on: boolean): void => {
    try {
      if (on) storage?.setItem(ANONYM_VIEW_KEY, "on");
      else storage?.removeItem(ANONYM_VIEW_KEY);
    } catch {
      // Private mode or blocked storage: the switch then lasts until reload.
    }
    emit();
  };
  const onStorage = (e: { key: string | null }): void => {
    // Another tab changed it (or cleared all storage).
    if (e.key === ANONYM_VIEW_KEY || e.key === null) emit();
  };

  return {
    get,
    set,
    clear: () => set(false),
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) target?.addEventListener("storage", onStorage);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) target?.removeEventListener("storage", onStorage);
      };
    },
  };
}

function browserStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export const anonymView: AnonymViewStore = createAnonymViewStore(
  browserStorage(),
  typeof window === "undefined" ? null : window,
);
